import { useState, useEffect } from 'react';
import {
  Modal,
  Stack,
  Group,
  Button,
  Text,
  Avatar,
  Box,
  Textarea,
  Loader,
  Center,
  Alert,
  Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { Mail, AlertTriangle } from 'react-feather';
import {
  cohortCoachesApi,
  type CohortCoachWithDetails,
  type AvailabilitySendPreviewRow,
} from '../../../services/api/cohortCoaches';

interface SendCoachReminderModalProps {
  opened: boolean;
  onClose: () => void;
  cohortId: string;
  coach: CohortCoachWithDetails | null;
  cohortName?: string;
  onSent?: () => void;
  emailDisabled?: boolean;
}

// Get initials from name
const getInitials = (firstName: string | null, lastName: string | null): string => {
  const f = firstName?.charAt(0) || '';
  const l = lastName?.charAt(0) || '';
  return (f + l).toUpperCase() || '?';
};

export function SendCoachReminderModal({
  opened,
  onClose,
  cohortId,
  coach,
  cohortName,
  onSent,
  emailDisabled,
}: SendCoachReminderModalProps) {
  const [sending, setSending] = useState(false);
  const [confirmOpened, setConfirmOpened] = useState(false);
  const [customMessage, setCustomMessage] = useState('');
  // MDF-233: a reminder needs nothing but the coach. It reuses the open
  // request and its magic link, so there is no ask to restate. What the modal
  // does need to know is whether an open request exists at all, because
  // without one there is nothing to remind anybody about.
  const [openRequest, setOpenRequest] = useState<AvailabilitySendPreviewRow | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  const fullName = coach
    ? `${coach.first_name || ''} ${coach.last_name || ''}`.trim() || coach.email
    : '';

  // MDF-233: look up the coach's open request when the modal opens, so the
  // modal can describe what is being reminded about and refuse cleanly when
  // there is nothing open.
  useEffect(() => {
    if (!opened || !cohortId || !coach) return;
    setLoadingPreview(true);
    cohortCoachesApi
      .previewAvailabilitySend(cohortId, [coach.user_id])
      .then(preview => {
        setOpenRequest(preview.open_requests[0] ?? null);
      })
      .catch(error => {
        console.error('Failed to load the open availability request:', error);
        setOpenRequest(null);
      })
      .finally(() => {
        setLoadingPreview(false);
      });
  }, [opened, cohortId, coach]);

  // Handle send reminder
  const handleSend = async () => {
    if (!coach) return;

    // MDF-233: no open request means there is nothing to remind about. Before
    // this change the button would happily create a brand new request here,
    // which is how a nudge turned into a duplicate card.
    if (!openRequest) {
      notifications.show({
        title: 'Cannot Send Reminder',
        message: `${fullName} has no open availability request. Use Send Availability Request on the Coaches tab first.`,
        color: 'red',
      });
      return;
    }

    setSending(true);
    try {
      // MDF-233: coach and message only. The API finds the open request, leaves
      // the ask exactly as it is, and resends the original link. on_existing
      // defaults to 'remind', so nothing is ever replaced from here.
      await cohortCoachesApi.sendAvailabilityRequest(cohortId, {
        coach_user_ids: [coach.user_id],
        email_message: customMessage || undefined,
      });

      notifications.show({
        title: 'Reminder Sent',
        message: `Availability reminder sent to ${fullName}`,
        color: 'green',
      });

      onSent?.();
      onClose();
      setCustomMessage('');
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to send reminder',
        color: 'red',
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Send Availability Reminder"
      size="md"
    >
      <Stack gap="md">
        {/* Coach Info */}
        {coach && (
          <Group gap="sm" p="sm" style={{ backgroundColor: 'var(--mantine-color-gray-0)', borderRadius: 6 }}>
            <Avatar size={40} radius="xl" color="gray">
              {getInitials(coach.first_name, coach.last_name)}
            </Avatar>
            <Box style={{ flex: 1 }}>
              <Text size="sm" fw={500}>{fullName}</Text>
              <Text size="xs" c="dimmed">{coach.email}</Text>
            </Box>
          </Group>
        )}

        {emailDisabled && (
          <Alert icon={<AlertTriangle size={16} />} color="yellow" variant="light">
            <Text size="sm">
              This email feature has been turned off for this cohort. To enable it, go to Cohort Settings &gt; Email Settings.
            </Text>
          </Alert>
        )}

        <Text size="sm" c="dimmed">
          Send a reminder email to {fullName} to complete their availability response
          {cohortName ? ` for ${cohortName}` : ''}.
          The email will include a link to their coach dashboard where they can submit their availability.
        </Text>

        <Textarea
          label="Custom Message (optional)"
          description="Add a personal note to the reminder email"
          placeholder="Hi, just following up on the availability request..."
          value={customMessage}
          onChange={(e) => setCustomMessage(e.currentTarget.value)}
          minRows={3}
          maxRows={5}
        />

        {/* MDF-233: say what the reminder actually covers, so nobody expects it
            to carry a change they just typed somewhere else. */}
        {!loadingPreview && openRequest && (
          <Alert color="gray" variant="light">
            <Text size="sm">
              This reminder repeats the request sent on{' '}
              <Text component="span" fw={500}>
                {new Date(openRequest.requested_at).toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                })}
              </Text>{' '}
              for {openRequest.requested_time_slots.length} time slot
              {openRequest.requested_time_slots.length === 1 ? '' : 's'}. It reuses the
              original link, so nothing new appears on their dashboard.
              {openRequest.reminder_count > 0
                ? ` Reminded ${openRequest.reminder_count} time${openRequest.reminder_count === 1 ? '' : 's'} already.`
                : ''}
            </Text>
          </Alert>
        )}

        {!loadingPreview && !openRequest && (
          <Alert color="orange" variant="light" icon={<AlertTriangle size={16} />}>
            <Text size="sm">
              {fullName} has no open availability request, so there is nothing to
              remind about. Use Send Availability Request on the Coaches tab first.
            </Text>
          </Alert>
        )}

        {loadingPreview ? (
          <Center py="md">
            <Loader size="sm" />
          </Center>
        ) : (
          <Group justify="flex-end" gap="sm">
            <Button variant="default" onClick={onClose}>
              Cancel
            </Button>
            <Tooltip
              label="This email feature has been turned off for this cohort. To enable it, go to Cohort Settings > Email Settings."
              disabled={!emailDisabled}
              multiline
              w={280}
            >
              <Button
                onClick={() => setConfirmOpened(true)}
                loading={sending}
                leftSection={<Mail size={16} />}
                disabled={!openRequest || !!emailDisabled}
              >
                Send Reminder
              </Button>
            </Tooltip>
          </Group>
        )}
      </Stack>

      {/* MED-646: Send confirmation dialog */}
      <Modal
        opened={confirmOpened}
        onClose={() => setConfirmOpened(false)}
        title="Confirm Email Send"
        size="sm"
        centered
        zIndex={301}
      >
        <Stack gap="md">
          <Alert icon={<AlertTriangle size={16} />} color="yellow" variant="light">
            <Text size="sm">
              You are about to send a reminder email to{' '}
              <Text component="span" fw={700}>
                {fullName}
              </Text>
              . This action cannot be undone.
            </Text>
          </Alert>
          <Group justify="flex-end" gap="sm">
            <Button
              variant="default"
              onClick={() => setConfirmOpened(false)}
              disabled={sending}
            >
              Cancel
            </Button>
            <Button
              color="medleyGreen"
              leftSection={<Mail size={14} />}
              onClick={() => {
                setConfirmOpened(false);
                handleSend();
              }}
              loading={sending}
              disabled={sending}
            >
              Confirm &amp; Send
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Modal>
  );
}

export default SendCoachReminderModal;
